import { createAction, handleActions } from 'redux-actions';
import { QUESTIONNAIRE_QUESTION_ANSWER_TYPES } from '../../constants/survey';
import http from '../../utility/http';
import { getItemIndex, replaceObjectInList } from '../../utility/helpers';
import {
  api_fill_survey,
  api_fill_attribute_review,
  api_fill_questionnaire_review,
  api_answer_on_attribute_question,
  api_answer_on_questionnaire_question,
  api_answer_on_text_reply_question,
  api_finish_review,
} from '../../constants/apiRoutes';
import {
  prepareSurveyData,
  prepareAttributeReviewData,
  prepareQuestionnaireReviewData,
  resumeAttributeReview,
  prepareAnswerTextResult,
  prepareAnswerCommentResult,
} from '../../utility/fillSurveyUtils';

// ------------------------------------
// Constants
// ------------------------------------

const SET_FILL_SURVEY = 'SET_FILL_SURVEY';
const SET_SURVEY_REVIEW = 'SET_SURVEY_REVIEW';
const SET_REVIEW_STEP = 'SET_REVIEW_STEP';
const SET_ATTRIBUTE_ANSWER = 'SET_ATTRIBUTE_ANSWER';
const SET_QUESTIONNAIRE_ANSWER = 'SET_QUESTIONNAIRE_ANSWER';
const SET_COMMENTS = 'SET_COMMENTS';
const SET_IS_INLINE_SURVEY = 'SET_IS_INLINE_SURVEY';
const RESET_FILL_SURVEY = 'RESET_FILL_SURVEY';
const SET_ANSWER_COMMENT = 'SET_ANSWER_COMMENT';

const initialState = {
  fillSurvey: {},
  review: {},
  surveyStep: 0,
  reviewStep: 0,
  isSurveyLoaded: false,
  isInlineSurvey: false,
};

// ------------------------------------
// Actions
// ------------------------------------

const setFillSurvey = createAction(SET_FILL_SURVEY);
const setSurveyReview = createAction(SET_SURVEY_REVIEW);
const setAttributeAnswer = createAction(SET_ATTRIBUTE_ANSWER);
const setQuestionnaireAnswer = createAction(SET_QUESTIONNAIRE_ANSWER);
const setAnswerComment = createAction(SET_ANSWER_COMMENT);
const resetFillSurvey = createAction(RESET_FILL_SURVEY);
export const setReviewStep = createAction(SET_REVIEW_STEP);
export const setComments = createAction(SET_COMMENTS);
export const setIsInlineSurvey = createAction(SET_IS_INLINE_SURVEY);

// ------------------------------------
// Reducers
// ------------------------------------

const reducers = {
  [SET_FILL_SURVEY]: (state, { payload }) => {
    return {
      ...state,
      isSurveyLoaded: true,
      fillSurvey: prepareSurveyData(payload),
    };
  },
  [SET_SURVEY_REVIEW]: (state, { payload }) => {
    return {
      ...state,
      review: payload.isQuestionnaire
        ? prepareQuestionnaireReviewData(payload)
        : prepareAttributeReviewData(payload),
      ...(payload.isQuestionnaire
        ? {}
        : resumeAttributeReview(state.fillSurvey.attributes, payload)),
    };
  },
  [SET_REVIEW_STEP]: (state, { payload }) => {
    return {
      ...state,
      reviewStep: payload,
    };
  },
  [SET_ATTRIBUTE_ANSWER]: (state, { payload }) => {
    const subjectIndex = getItemIndex(
      state.review.subjects,
      payload?.subjectId
    );

    if (
      payload.answer === null &&
      state.review.subjects[subjectIndex].replied?.[payload.question]?.reply
    ) {
      const subject = {
        ...state.review.subjects[subjectIndex],
        replied: {
          ...state.review.subjects[subjectIndex].replied,
          [payload.question]: {
            reply: null,
            not_applicable: false,
          },
        },
      };

      return {
        ...state,
        review: {
          ...state.review,
          results: {
            ...state.review.results,
            [payload.review_tracking]: {
              ...state.review.results[payload.review_tracking],
              [payload.question]: payload.answer,
            },
          },
          subjects: replaceObjectInList(
            state.review.subjects,
            subjectIndex,
            subject
          ),
        },
      };
    }

    return {
      ...state,
      review: {
        ...state.review,
        results: {
          ...state.review.results,
          [payload.review_tracking]: {
            ...state.review.results[payload.review_tracking],
            [payload.question]: payload.answer,
          },
        },
      },
    };
  },
  [SET_ANSWER_COMMENT]: (state, { payload }) => {
    const subjectIndex = getItemIndex(
      state.review.subjects,
      payload?.subjectId
    );

    const subject = {
      ...state.review.subjects[subjectIndex],
      replied: {
        ...state.review.subjects[subjectIndex].replied,
        [payload.question]: {
          reply: payload.reply || null,
          not_applicable: payload.not_applicable,
        },
      },
    };

    return {
      ...state,
      review: {
        ...state.review,
        subjects: replaceObjectInList(
          state.review.subjects,
          subjectIndex,
          subject
        ),
      },
    };
  },
  [SET_COMMENTS]: (state, { payload }) => {
    return {
      ...state,
      review: {
        ...state.review,
        comments: payload,
      },
    };
  },
  [SET_QUESTIONNAIRE_ANSWER]: (state, { payload }) => {
    return {
      ...state,
      review: {
        ...state.review,
        results: {
          ...state.review.results,
          [payload.review_tracking]: {
            ...state.review.results[payload.review_tracking],
            [payload.question]: {
              [QUESTIONNAIRE_QUESTION_ANSWER_TYPES[payload.questionType]]:
                payload[
                  QUESTIONNAIRE_QUESTION_ANSWER_TYPES[payload.questionType]
                ],
            },
          },
        },
      },
    };
  },
  [SET_IS_INLINE_SURVEY]: (state, { payload }) => ({
    ...state,
    isInlineSurvey: payload,
  }),
  [RESET_FILL_SURVEY]: (state, { payload }) => {
    return payload;
  },
};

// ------------------------------------
// API calls
// ------------------------------------

export const getFillSurvey = (dispatch, token) => {
  return http.get(api_fill_survey(token)).then(
    ({ data }) => dispatch(setFillSurvey(data)),
    () => dispatch(resetFillSurvey({ ...initialState, isSurveyLoaded: true }))
  );
};

export const getSurveyReview = (
  dispatch,
  token,
  reviewId,
  isQuestionnaire = false
) => {
  return http
    .get(
      isQuestionnaire
        ? api_fill_questionnaire_review(token, reviewId)
        : api_fill_attribute_review(token, reviewId)
    )
    .then(
      ({ data }) => dispatch(setSurveyReview({ ...data, isQuestionnaire })),
      () => dispatch(resetFillSurvey({ ...initialState, isSurveyLoaded: true }))
    );
};

export const setAttributeAnswerResult = (dispatch, token, answer) => {
  const { subjectId, ...rest } = answer;
  return http.post(api_answer_on_attribute_question(token), { ...rest }).then(
    () => dispatch(setAttributeAnswer(answer)),
    () => dispatch(resetFillSurvey({ ...initialState, isSurveyLoaded: true }))
  );
};

export const setAnswerCommentResult = (dispatch, token, payload) => {
  const data = prepareAnswerCommentResult(payload);
  return http.post(api_answer_on_text_reply_question(token), data).then(
    () => dispatch(setAnswerComment({ ...data, subjectId: payload.subjectId })),
    () => dispatch(resetFillSurvey({ ...initialState, isSurveyLoaded: true }))
  );
};

export const setAnswerTextResult = (dispatch, token, payload) => {
  return http
    .post(
      api_answer_on_text_reply_question(token),
      prepareAnswerTextResult(payload)
    )
    .then(
      () => dispatch(setAttributeAnswer(payload)),
      () => dispatch(resetFillSurvey({ ...initialState, isSurveyLoaded: true }))
    );
};

export const setQuestionnaireAnswerResult = (dispatch, token, answer) => {
  const { questionType, ...rest } = answer;
  return http.post(api_answer_on_questionnaire_question(token), rest).then(
    () => {
      return dispatch(setQuestionnaireAnswer(answer));
    },
    () => dispatch(resetFillSurvey({ ...initialState, isSurveyLoaded: true }))
  );
};

export const submitReview = (dispatch, token, comments) =>
  http.post(api_finish_review(token), comments);

export default handleActions(reducers, initialState);

// ------------------------------------
// "plain" action functions
// ------------------------------------

export const clearFillSurvey = isInlineSurvey =>
  resetFillSurvey({ ...initialState, isInlineSurvey });
